import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { SECURITY_DEFAULT_DATE_RANGE } from 'src/mindtime/constants/securityFilters';

export type SecurityPageFilterValues = Record<string, string>;

type SecurityPageState = {
  filters: SecurityPageFilterValues;
  search: string;
};

export type SecurityFiltersState = {
  global: {
    dateRange: string;
    selectedClientId: string | null;
  };
  pages: Record<string, SecurityPageState>;
};

const initialState: SecurityFiltersState = {
  global: {
    dateRange: SECURITY_DEFAULT_DATE_RANGE,
    selectedClientId: null,
  },
  pages: {},
};

const securityFiltersSlice = createSlice({
  name: 'securityFilters',
  initialState,
  reducers: {
    ensurePageFilters: (
      state,
      action: PayloadAction<{ pageKey: string; filters: SecurityPageFilterValues; search: string }>
    ) => {
      const { pageKey, filters, search } = action.payload;
      if (!state.pages[pageKey]) {
        state.pages[pageKey] = { filters: { ...filters }, search };
      }
    },
    setPageFilter: (
      state,
      action: PayloadAction<{ pageKey: string; key: string; value: string }>
    ) => {
      const { pageKey, key, value } = action.payload;
      if (!state.pages[pageKey]) {
        state.pages[pageKey] = { filters: {}, search: '' };
      }
      if (value) {
        state.pages[pageKey].filters[key] = value;
      } else {
        delete state.pages[pageKey].filters[key];
      }
    },
    setPageSearch: (state, action: PayloadAction<{ pageKey: string; search: string }>) => {
      const { pageKey, search } = action.payload;
      if (!state.pages[pageKey]) {
        state.pages[pageKey] = { filters: {}, search };
        return;
      }
      state.pages[pageKey].search = search;
    },
    resetPageFilters: (state, action: PayloadAction<{ pageKey: string; search: string }>) => {
      const { pageKey, search } = action.payload;
      state.pages[pageKey] = { filters: {}, search };
    },
    setSecurityDateRange: (state, action: PayloadAction<string>) => {
      state.global.dateRange = action.payload;
    },
    setSecuritySelectedClientId: (state, action: PayloadAction<string | null>) => {
      state.global.selectedClientId = action.payload;
    },
    resetSecurityFilters: () => initialState,
  },
});

export const {
  ensurePageFilters,
  setPageFilter,
  setPageSearch,
  resetPageFilters,
  setSecurityDateRange,
  setSecuritySelectedClientId,
  resetSecurityFilters,
} = securityFiltersSlice.actions;

export default securityFiltersSlice.reducer;
